// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Box from "../../resources/layout-and-structure/Box.res.js";
import * as I18n from "../../primitives/I18n.res.js";
import * as Badge from "../../resources/feedback-indicators/Badge.res.js";
import * as React from "react";
import * as Button from "../../resources/actions/Button.res.js";
import * as Future from "rescript-future/src/Future.res.js";
import * as Inline from "../../resources/layout-and-structure/Inline.res.js";
import * as TextLink from "../../resources/navigation/TextLink.res.js";
import * as SearchBar from "../../resources/selection-and-input/SearchBar.res.js";
import * as TableView from "../../resources/tables/TableView.res.js";
import * as TextStyle from "../../resources/typography/TextStyle.res.js";
import * as EmptyState from "../../resources/layout-and-structure/EmptyState.res.js";
import * as AsyncResult from "../../primitives/AsyncResult.res.js";
import * as Stdlib_Math from "@rescript/runtime/lib/es6/Stdlib_Math.js";
import * as Illustration from "../../resources/images-and-icons/Illustration.res.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as BillingAccount from "./BillingAccount.res.js";
import * as SettingsRoutes from "../../app/Settings/SettingsRoutes.res.js";
import * as ShowAllDataLink from "../../resources/navigation/ShowAllDataLink.res.js";
import * as LegacyPagination from "../../resources/navigation/LegacyPagination.res.js";
import * as JsxRuntime from "react/jsx-runtime";

function keyExtractor(row) {
  return row.number;
}

function sanitize(string) {
  return string.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9 ]/g, "");
}

function match(row, query) {
  if (query === "" || sanitize(row.number).includes(sanitize(query)) || sanitize(row.date).includes(sanitize(query))) {
    return true;
  } else {
    return sanitize(row.total.toString()).includes(sanitize(query));
  }
}

function fromQueryItem(queryItem) {
  return {
    number: queryItem.number,
    date: I18n.dateTimeFormat(undefined, "short", undefined, undefined, undefined, undefined, undefined, undefined, undefined, queryItem.date),
    total: queryItem.total,
    status: queryItem.status,
    paymentLink: queryItem.paymentLink,
    pdfLink: Stdlib_Option.getOr(queryItem.pdfLink, "")
  };
}

let TableRow = {
  keyExtractor: keyExtractor,
  sanitize: sanitize,
  match: match,
  fromQueryItem: fromQueryItem
};

function totalPages(rows) {
  return Stdlib_Math.Int.ceil(rows.length / 10);
}

function search(rows, searchQuery) {
  return rows.filter(row => match(row, searchQuery));
}

function paginate(rows, currentPage) {
  return rows.slice((currentPage - 1 | 0) * 10 | 0, currentPage * 10 | 0);
}

function fromQueryAllShops(queryAllShops) {
  return queryAllShops.map(fromQueryItem);
}

let TableRows = {
  rowsPerPage: 10,
  totalPages: totalPages,
  search: search,
  paginate: paginate,
  fromQueryAllShops: fromQueryAllShops
};

function BillingAccountInvoicesTable$InvoiceStatusBadge(props) {
  let __size = props.size;
  let value = props.value;
  let size = __size !== undefined ? __size : "normal";
  let variation;
  if (value !== undefined) {
    switch (value) {
      case "Open" :
        variation = "warning";
        break;
      case "Paid" :
        variation = "success";
        break;
      case "PaidViaCreditNote" :
      case "Uncollectible" :
      case "Void" :
        variation = "information";
        break;
    }
  } else {
    variation = "information";
  }
  return JsxRuntime.jsx(Badge.make, {
    children: I18n.t(BillingAccount.InvoiceStatus.toString(Stdlib_Option.getOr(value, "Void"))),
    size: size,
    variation: variation
  });
}

let InvoiceStatusBadge = {
  make: BillingAccountInvoicesTable$InvoiceStatusBadge
};

let tableColumns = [
  {
    key: "number",
    name: I18n.t("Number"),
    layout: {
      minWidth: {
        NAME: "px",
        VAL: 260
      },
      width: {
        NAME: "fr",
        VAL: 1.5
      }
    },
    render: param => {
      let match = param.data;
      return JsxRuntime.jsx(Box.make, {
        children: JsxRuntime.jsx(TextLink.make, {
          text: match.number,
          to: {
            TAG: "Url",
            _0: new URL(match.pdfLink)
          }
        }),
        spaceTop: "small",
        spaceBottom: "small"
      });
    }
  },
  {
    key: "date",
    name: I18n.t("Date"),
    layout: {
      minWidth: {
        NAME: "px",
        VAL: 160
      }
    },
    render: param => JsxRuntime.jsx(TextStyle.make, {
      children: param.data.date
    })
  },
  {
    key: "amount",
    name: I18n.t("Amount"),
    layout: {
      minWidth: {
        NAME: "px",
        VAL: 160
      }
    },
    render: param => JsxRuntime.jsx(TextStyle.make, {
      children: I18n.currencyFormat(undefined, "EUR", 2, 2, param.data.total)
    })
  },
  {
    key: "status",
    name: I18n.t("Status"),
    layout: {
      minWidth: {
        NAME: "px",
        VAL: 250
      }
    },
    render: param => {
      let match = param.data;
      let paymentLink = match.paymentLink;
      let status = match.status;
      if (status === "Open" && paymentLink !== undefined) {
        return JsxRuntime.jsxs(Inline.make, {
          children: [
            JsxRuntime.jsx(BillingAccountInvoicesTable$InvoiceStatusBadge, {
              value: status
            }),
            JsxRuntime.jsx(TextLink.make, {
              text: I18n.t("Pay now") + "→",
              to: {
                TAG: "Url",
                _0: new URL(paymentLink)
              },
              highlighted: true
            })
          ],
          space: "normal",
          alignY: "center"
        });
      }
      return JsxRuntime.jsx(BillingAccountInvoicesTable$InvoiceStatusBadge, {
        value: status
      });
    }
  },
  {
    key: "link",
    name: "",
    layout: {
      minWidth: {
        NAME: "px",
        VAL: 20
      },
      alignX: "flexEnd"
    },
    render: param => JsxRuntime.jsx(Button.make, {
      shape: "pill",
      icon: "download_v1",
      href: param.data.pdfLink
    })
  }
];

let initialState_asyncResult = AsyncResult.notAsked();

let initialState = {
  searchQuery: "",
  currentPage: 1,
  asyncResult: initialState_asyncResult
};

function make(prevState, action) {
  switch (action.TAG) {
    case "SearchQueryChanged" :
      return {
        searchQuery: action._0,
        currentPage: 1,
        asyncResult: prevState.asyncResult
      };
    case "AsyncResultGet" :
      return {
        searchQuery: prevState.searchQuery,
        currentPage: 1,
        asyncResult: action._0
      };
    case "Paginated" :
      let totalPages = action._1;
      let match = prevState.currentPage;
      let tmp;
      switch (action._0) {
        case "First" :
          tmp = 1;
          break;
        case "Prev" :
          tmp = match !== 1 ? prevState.currentPage - 1 | 0 : 1;
          break;
        case "Next" :
          tmp = match >= totalPages ? match : prevState.currentPage + 1 | 0;
          break;
        case "Last" :
          tmp = totalPages;
          break;
      }
      return {
        searchQuery: prevState.searchQuery,
        currentPage: tmp,
        asyncResult: prevState.asyncResult
      };
  }
}

let Reducer = {
  initialState: initialState,
  make: make
};

function BillingAccountInvoicesTable(props) {
  let __preview = props.preview;
  let invoicesRequest = props.invoicesRequest;
  let shopId = props.shopId;
  let preview = __preview !== undefined ? __preview : false;
  let match = React.useReducer(make, initialState);
  let state = match[0];
  let currentPage = state.currentPage;
  let searchQuery = state.searchQuery;
  let dispatch = match[1];
  React.useEffect(() => {
    let request = Future.mapError(invoicesRequest(shopId), undefined, param => {});
    dispatch({
      TAG: "AsyncResultGet",
      _0: AsyncResult.loading()
    });
    Future.get(request, result => dispatch({
      TAG: "AsyncResultGet",
      _0: AsyncResult.done(result)
    }));
    return () => Future.cancel(request);
  }, [shopId]);
  let tableRows = AsyncResult.mapOk(state.asyncResult, fromQueryAllShops);
  let searchedAndFilteredTableRows = AsyncResult.mapOk(tableRows, rows => {
    if (preview) {
      return rows.slice(0, 5);
    } else {
      return search(rows, searchQuery);
    }
  });
  let totalPages$1;
  if (typeof searchedAndFilteredTableRows !== "object") {
    totalPages$1 = 1;
  } else if (searchedAndFilteredTableRows.TAG === "Reloading") {
    let rows = searchedAndFilteredTableRows._0;
    totalPages$1 = rows.TAG === "Ok" ? totalPages(rows._0) : 1;
  } else {
    let rows$1 = searchedAndFilteredTableRows._0;
    totalPages$1 = rows$1.TAG === "Ok" ? totalPages(rows$1._0) : 1;
  }
  let searchedAndPaginatedTableRows = AsyncResult.mapOk(searchedAndFilteredTableRows, rows => paginate(rows, currentPage));
  let onRequestSearch = queryString => dispatch({
    TAG: "SearchQueryChanged",
    _0: queryString
  });
  let onRequestPaginate = paginateAction => dispatch({
    TAG: "Paginated",
    _0: paginateAction,
    _1: totalPages$1
  });
  let match$1 = state.asyncResult;
  let placeholderNoRows;
  let exit = 0;
  if (typeof searchedAndPaginatedTableRows !== "object" || searchedAndPaginatedTableRows.TAG !== "Done") {
    exit = 1;
  } else {
    let match$2 = searchedAndPaginatedTableRows._0;
    if (match$2.TAG === "Ok" && match$2._0.length === 0) {
      if (typeof match$1 !== "object" || match$1.TAG !== "Done") {
        placeholderNoRows = EmptyState.error;
      } else {
        let rows$2 = match$1._0;
        if (rows$2.TAG === "Ok") {
          if (rows$2._0.length !== 0 && preview === false) {
            placeholderNoRows = JsxRuntime.jsx(EmptyState.make, {
              illustration: Illustration.noResultFound,
              title: I18n.t("No result were found."),
              text: I18n.t("Try again with another keyword or:"),
              children: JsxRuntime.jsx(Button.make, {
                label: I18n.t("Clear search query"),
                onPress: () => dispatch({
                  TAG: "SearchQueryChanged",
                  _0: ""
                })
              })
            });
          } else {
            exit = 1;
          }
        } else {
          placeholderNoRows = EmptyState.error;
        }
      }
    } else {
      exit = 1;
    }
  }
  if (exit === 1) {
    if (typeof match$1 !== "object" || match$1.TAG !== "Done") {
      placeholderNoRows = EmptyState.error;
    } else {
      let match$3 = match$1._0;
      placeholderNoRows = match$3.TAG === "Ok" && match$3._0.length === 0 ? JsxRuntime.jsx(EmptyState.make, {
          illustration: Illustration.noResultFound,
          title: I18n.t("Sorry, no invoices were found.")
        }) : EmptyState.error;
    }
  }
  let searchBar = preview ? null : JsxRuntime.jsx(Box.make, {
      children: JsxRuntime.jsx(SearchBar.make, {
        placeholder: I18n.t("Search an invoice"),
        value: searchQuery,
        onChange: onRequestSearch
      }),
      spaceX: "large",
      spaceBottom: "xmedium"
    });
  let tmp;
  if (typeof tableRows !== "object" || tableRows.TAG !== "Done") {
    tmp = null;
  } else {
    let rows$3 = tableRows._0;
    tmp = preview ? (
        rows$3.TAG === "Ok" && preview && rows$3._0.length > 5 ? JsxRuntime.jsx(ShowAllDataLink.make, {
            label: I18n.t("Show all invoices"),
            href: SettingsRoutes.billingAccountInvoicesRoute(shopId)
          }) : null
      ) : JsxRuntime.jsx(LegacyPagination.make, {
        currentPage: currentPage,
        totalPages: totalPages$1,
        onRequestPaginate: onRequestPaginate
      });
  }
  return JsxRuntime.jsxs(JsxRuntime.Fragment, {
    children: [
      JsxRuntime.jsx(TableView.make, {
        data: searchedAndPaginatedTableRows,
        columns: tableColumns,
        keyExtractor: keyExtractor,
        placeholderEmptyState: placeholderNoRows,
        searchBar: searchBar
      }),
      tmp
    ]
  });
}

let make$1 = BillingAccountInvoicesTable;

export {
  TableRow,
  TableRows,
  InvoiceStatusBadge,
  tableColumns,
  Reducer,
  make$1 as make,
}
/* tableColumns Not a pure module */
